import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../api/store";
import { AmountOfLoansInput, AmountOfLoansState } from "./amount-loans.state";
import { SubTypeOption } from "../../../api/gms/group-category/group-category-api-state";

const initialState: AmountOfLoansState = {
    selectedAmountOfLoans: null,
    selectedAmountOfLoansInput: null,
    riders: [],
    selectFAmountOfLoans: null,
    minAmount: null,
    maxAmount: null,
    loanPortfolio: null
};

const amountOfLoansSlice = createSlice({
    name: "amountOfLoansSlice",
    initialState,
    reducers: {
        setUpdateAmount: (state, action: PayloadAction<{type: 'min' | 'max' | 'port', amount: number | null}>) => {
            const { type, amount} = action.payload;
            if(type === 'min'){
                state.minAmount = amount;
                return
            }
            if(type === 'max'){
                state.maxAmount = amount;
                return
            }
            if(type === 'port'){
                state.loanPortfolio = amount;
                return
            }
        },
        resetMinMaxPortfolioAmount: (state) => {
            state.minAmount = null;
            state.maxAmount = null;
            state.loanPortfolio = null;
        },
        setSelectedAmountOfLoansBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedAmountOfLoans = action.payload;
        },
        setSelectedAmountOfLoansInput: (
            state,
            action: PayloadAction<{ label: string; value: string | number }>
        ) => {
            let rawValue = String(action.payload.value).replace(/[^0-9.]/g, "");

            // Handle multiple dots
            const parts = rawValue.split(".");
            if (parts.length > 2) {
                rawValue = parts[0] + "." + parts[1];
            }

            let [integerPart, decimalPart] = rawValue.split(".");

            // Limit decimal to 2 digits
            if (decimalPart !== undefined) {
                decimalPart = decimalPart.slice(0, 2);
            }

            // Format integer part with commas
            integerPart = integerPart
                ? Number(integerPart).toLocaleString("en-US")
                : "";

            const formattedValue =
                decimalPart !== undefined
                    ? `${integerPart}.${decimalPart}`
                    : integerPart;

            state.selectedAmountOfLoansInput = {
                label: action.payload.label,
                fAmount: formattedValue,
            };
        },

        addAmountOfLoansRider: (state, action: PayloadAction<{ id: number; label: string }>) => {
            if (!state.selectedAmountOfLoans || !state.selectedAmountOfLoansInput) return;

            const { id, label } = action.payload;
            const exists = state.riders.some(r => r.id === id);
            if (exists) return;

            state.riders.push({
                id,
                label,
                rider: {
                    label: state.selectedAmountOfLoans.name,
                    fAmount: state.selectedAmountOfLoansInput.fAmount,
                },
            });
        },

        updateAmountOfLoansRiderAmount: (
            state,
            action: PayloadAction<{
                rider_id: number;
                amount: string;
            }>
        ) => {
            const { rider_id, amount } = action.payload;

            const rider = state.riders.find(r => r.id === rider_id);

            if (rider) {
                rider.rider.fAmount = amount;
            }
        },

        removeAmountOfLoansRider: (state, action: PayloadAction<number>) => {
            state.riders = state.riders.filter(r => r.id !== action.payload);
        },

        updateAmountOfLoansRiderFAmount: (state, action: PayloadAction<{ riderId: number; fAmount: string | number }>) => {
            const { riderId, fAmount } = action.payload;
            const riderItem = state.riders.find(r => r.id === riderId);
            if (!riderItem) return;
            riderItem.rider.fAmount = fAmount;
        },

        resetAmountOfLoans: () => ({ ...initialState }),

        resetInputAmountAndRiders: (state) => {
            state.selectedAmountOfLoansInput = null;
            state.riders = [];
        },
        setSelectedAmountOfLoansInputFromAPI: (state, action: PayloadAction<AmountOfLoansInput | null>) => {
            state.selectedAmountOfLoansInput = action.payload;
        },
        setFAmountOfLoans: (state, action: PayloadAction<number | null>) => {
            state.selectFAmountOfLoans = action.payload;
        },
        resetLoanAmountRiders: (state) => {
            state.riders = [];
        },
    },
});

export const {
    setUpdateAmount,
    updateAmountOfLoansRiderAmount,
    resetMinMaxPortfolioAmount,
    setSelectedAmountOfLoansBox,
    setSelectedAmountOfLoansInput,
    addAmountOfLoansRider,
    removeAmountOfLoansRider,
    updateAmountOfLoansRiderFAmount,
    resetAmountOfLoans,
    resetInputAmountAndRiders,
    resetLoanAmountRiders,
    setSelectedAmountOfLoansInputFromAPI,
    setFAmountOfLoans,
} = amountOfLoansSlice.actions;

export const selectAmountOfLoans = (state: RootState) => state.amountOfLoansSlice.selectedAmountOfLoans;
// export const selectAmountOfLoansId = (state: RootState) => state.amountOfLoansSlice.selectedAmountOfLoans?.id;
// export const selectAmountOfLoansName = (state: RootState) => state.amountOfLoansSlice.selectedAmountOfLoans?.name;
export const selectAmountOfLoansInput = (state: RootState) => state.amountOfLoansSlice.selectedAmountOfLoansInput;
export const selectAmountOfLoansRiders = (state: RootState) => state.amountOfLoansSlice.riders;
export const selectAmountOfLoansFAmount = (state: RootState) => state.amountOfLoansSlice.selectFAmountOfLoans;
export const selectMinAmount = (state: RootState) => state.amountOfLoansSlice.minAmount;
export const selectMaxAmount = (state: RootState) => state.amountOfLoansSlice.maxAmount;
export const selectLoanPortfolio = (state: RootState) => state.amountOfLoansSlice.loanPortfolio;

export default amountOfLoansSlice.reducer;